<template>
  <figure class="image-slide">
    <div class="image-slide__zoomable" ref="zoomable">
      <img :alt="props.image.caption" :src="props.image.url" />
    </div>
    <figcaption>{{ props.image.caption }}</figcaption>
  </figure>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Props } from './props'

const props = defineProps(Props())
const zoomable = ref(null)

let scale = 1
let lastScale = 1
let startDistance = 0
let posX = 0
let posY = 0
let lastX = 0
let lastY = 0
let isZooming = false

// отправляем состояние в viewer.vue
const emitZoomState = (state) => {
  zoomable.value?.dispatchEvent(new CustomEvent('zoom-change', { detail: state, bubbles: true }))
}

function getDistance(touches) {
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.sqrt(dx * dx + dy * dy)
}

function updateTransform(withTransition = false) {
  if (!zoomable.value) return

  const img = zoomable.value.querySelector('img')
  if (!img) return

  const rect = zoomable.value.getBoundingClientRect()
  const imgWidth = rect.width
  const imgHeight = rect.height

  const maxX = (imgWidth * scale - imgWidth) / 2
  const maxY = (imgHeight * scale - imgHeight) / 2

  // ограничиваем выход за края
  posX = Math.min(Math.max(posX, -maxX), maxX)
  posY = Math.min(Math.max(posY, -maxY), maxY)

  zoomable.value.style.transition = withTransition ? 'transform 0.3s ease-out' : 'none'
  zoomable.value.style.transform = `translate(${posX}px, ${posY}px) scale(${scale})`
}

const isAtEdge = () => {
  const rect = zoomable.value.getBoundingClientRect()
  const imgWidth = rect.width
  const maxX = (imgWidth * scale - imgWidth) / 2
  return {
    left: posX <= -maxX + 2,
    right: posX >= maxX - 2
  }
}

const onTouchStart = (e) => {
  if (e.touches.length === 2) {
    isZooming = true
    startDistance = getDistance(e.touches)
  } else if (scale > 1 && e.touches.length === 1) {
    lastX = e.touches[0].clientX
    lastY = e.touches[0].clientY
  }
}

const onTouchMove = (e) => {
  if (isZooming && e.touches.length === 2) {
    const newDistance = getDistance(e.touches)
    scale = Math.min(Math.max((newDistance / startDistance) * lastScale, 1), 4)
    updateTransform()
    emitZoomState({ zooming: scale > 1, edge: isAtEdge() })
  } else if (scale > 1 && e.touches.length === 1) {
    const dx = e.touches[0].clientX - lastX
    const dy = e.touches[0].clientY - lastY
    posX += dx
    posY += dy
    lastX = e.touches[0].clientX
    lastY = e.touches[0].clientY
    updateTransform()
    emitZoomState({ zooming: scale > 1, edge: isAtEdge() })
  }
}

const onTouchEnd = (e) => {
  if (isZooming && e.touches.length < 2) {
    lastScale = scale
    isZooming = false
  }
  if (scale === 1) {
    posX = posY = 0
    updateTransform(true)
    emitZoomState({ zooming: false, edge: { left: true, right: true } })
  }
}

const resetZoom = () => {
  scale = 1
  lastScale = 1
  posX = 0
  posY = 0
  updateTransform(true)
  emitZoomState({ zooming: false, edge: { left: true, right: true } })
}

onMounted(() => {
  zoomable.value.addEventListener('touchstart', onTouchStart, { passive: false })
  zoomable.value.addEventListener('touchmove', onTouchMove, { passive: false })
  zoomable.value.addEventListener('touchend', onTouchEnd)
})

onBeforeUnmount(() => {
  zoomable.value.removeEventListener('touchstart', onTouchStart)
  zoomable.value.removeEventListener('touchmove', onTouchMove)
  zoomable.value.removeEventListener('touchend', onTouchEnd)
})

defineExpose({ resetZoom })
</script>

<style lang="scss" scoped>
  @import './styles.scss';
</style>
